import { useState, useCallback, useEffect } from "react";
import { useForm, type Resolver } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "expo-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { step3Schema, type Step3Values } from "../schemas/preVettingSchema";
import { usePreVettingDraft } from "./usePreVettingDraft";
import { useDebouncedDraftSave } from "./useDebouncedDraftSave";
import { useRestoredDocumentFile } from "@/hooks/useRestoredDocumentFile";
import { preVettingApi } from "@/lib/api/preVetting";
import { localizationApi } from "@/lib/api/localization";
import { useI18n } from "@/context/I18nContext";
import type { SupportedLocale, TranslationKeys } from "@chinese-vendor/i18n";
import type { FileUploadState, SelectedFile } from "@/components/ui/FileUpload";

export function usePreVettingStep3Form() {
  const router = useRouter();
  const { locale: uiLocale } = useI18n();
  const { draftQuery, saveDraftMutation } = usePreVettingDraft();
  const scheduleSave = useDebouncedDraftSave(saveDraftMutation.mutateAsync);

  const form = useForm<Step3Values>({
    resolver: zodResolver(step3Schema) as unknown as Resolver<Step3Values>,
    defaultValues: {
      industry: [],
      product_subcategories: [],
      iso_certification_document: null,
      ce_marking_document: null,
      quality_test_report_document: null,
      eco_certification_document: null,
    },
    mode: "onBlur",
  });

  const [isoCertFile, setIsoCertFile] = useState<SelectedFile | null>(null);
  const [isoCertUploadState, setIsoCertUploadState] = useState<FileUploadState>("idle");
  const [isoCertUploadError, setIsoCertUploadError] = useState<TranslationKeys | undefined>(
    undefined
  );
  const [ceMarkingFile, setCeMarkingFile] = useState<SelectedFile | null>(null);
  const [ceMarkingUploadState, setCeMarkingUploadState] = useState<FileUploadState>("idle");
  const [ceMarkingUploadError, setCeMarkingUploadError] = useState<TranslationKeys | undefined>(
    undefined
  );
  const [qualityReportFile, setQualityReportFile] = useState<SelectedFile | null>(null);
  const [qualityReportUploadState, setQualityReportUploadState] = useState<FileUploadState>("idle");
  const [qualityReportUploadError, setQualityReportUploadError] = useState<
    TranslationKeys | undefined
  >(undefined);
  const [ecoCertFile, setEcoCertFile] = useState<SelectedFile | null>(null);
  const [ecoCertUploadState, setEcoCertUploadState] = useState<FileUploadState>("idle");
  const [ecoCertUploadError, setEcoCertUploadError] = useState<TranslationKeys | undefined>(
    undefined
  );
  const [declarationChecked, setDeclarationChecked] = useState(false);
  const [declarationError, setDeclarationError] = useState(false);

  // Restore file previews from saved S3 keys on draft load
  const step3 = draftQuery.isSuccess ? draftQuery.data?.step3 : null;
  const { file: restoredIsoCert, uploadState: restoredIsoCertState } = useRestoredDocumentFile(
    step3?.iso_certification_document ?? null
  );
  const { file: restoredCeMarking, uploadState: restoredCeMarkingState } = useRestoredDocumentFile(
    step3?.ce_marking_document ?? null
  );
  const { file: restoredQualityReport, uploadState: restoredQualityReportState } =
    useRestoredDocumentFile(step3?.quality_test_report_document ?? null);
  const { file: restoredEcoCert, uploadState: restoredEcoCertState } = useRestoredDocumentFile(
    step3?.eco_certification_document ?? null
  );

  useEffect(() => {
    if (restoredIsoCert && isoCertUploadState === "idle" && !isoCertFile) {
      setIsoCertFile(restoredIsoCert);
      setIsoCertUploadState(restoredIsoCertState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoredIsoCert]);
  useEffect(() => {
    if (restoredCeMarking && ceMarkingUploadState === "idle" && !ceMarkingFile) {
      setCeMarkingFile(restoredCeMarking);
      setCeMarkingUploadState(restoredCeMarkingState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoredCeMarking]);
  useEffect(() => {
    if (restoredQualityReport && qualityReportUploadState === "idle" && !qualityReportFile) {
      setQualityReportFile(restoredQualityReport);
      setQualityReportUploadState(restoredQualityReportState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoredQualityReport]);
  useEffect(() => {
    if (restoredEcoCert && ecoCertUploadState === "idle" && !ecoCertFile) {
      setEcoCertFile(restoredEcoCert);
      setEcoCertUploadState(restoredEcoCertState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoredEcoCert]);

  // ── Picker modal state ─────────────────────────────────────────────────────
  const [industryPickerVisible, setIndustryPickerVisible] = useState(false);
  const [subcategoryPickerVisible, setSubcategoryPickerVisible] = useState(false);
  // Separate picker locale so vendors can browse categories in Chinese
  const [pickerLocale, setPickerLocale] = useState<SupportedLocale>(uiLocale);

  // ── Industries query ───────────────────────────────────────────────────────
  const industriesQuery = useQuery({
    queryKey: ["industries", pickerLocale],
    queryFn: () => localizationApi.getIndustries(pickerLocale),
    staleTime: 5 * 60 * 1000,
    enabled: true,
  });

  // ── Product subcategories query (filtered by selected industry ids) ─────────
  const selectedIndustryIds = form.watch("industry");
  const subcategoriesQuery = useQuery({
    queryKey: ["productCategories", pickerLocale, selectedIndustryIds],
    queryFn: () => localizationApi.getProductCategories(pickerLocale, selectedIndustryIds),
    staleTime: 5 * 60 * 1000,
    enabled: true,
  });

  const togglePickerLocale = useCallback(() => {
    setPickerLocale((prev) => (prev === "zh-CN" ? "en" : "zh-CN"));
  }, []);

  // Populate form once draft has loaded
  useEffect(() => {
    if (draftQuery.isSuccess && draftQuery.data?.step3) {
      const draftStep3 = draftQuery.data.step3 as Partial<Step3Values>;
      const current = form.getValues();
      form.reset({
        ...current,
        ...draftStep3,
        industry: draftStep3.industry ?? current.industry,
        product_subcategories: draftStep3.product_subcategories ?? current.product_subcategories,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftQuery.isSuccess]);

  const onFieldBlur = useCallback(() => {
    scheduleSave({ step3: form.getValues() });
  }, [scheduleSave, form]);

  const saveIndustries = useCallback(
    (ids: string[]) => {
      form.setValue("industry", ids, { shouldValidate: true });
      onFieldBlur();
    },
    [form, onFieldBlur]
  );

  const saveSubcategories = useCallback(
    (ids: string[]) => {
      form.setValue("product_subcategories", ids, { shouldValidate: true });
      onFieldBlur();
    },
    [form, onFieldBlur]
  );

  const handleIsoCertFile = useCallback(
    async (file: SelectedFile) => {
      setIsoCertFile(file);
      setIsoCertUploadState("uploading");
      setIsoCertUploadError(undefined);
      try {
        const key = await preVettingApi.uploadDocument(file);
        form.setValue("iso_certification_document", key);
        onFieldBlur();
        setIsoCertUploadState("success");
      } catch {
        setIsoCertUploadState("error");
        setIsoCertUploadError("fileUpload.uploadFailed");
      }
    },
    [form, onFieldBlur]
  );

  const handleIsoCertRemove = useCallback(() => {
    setIsoCertFile(null);
    setIsoCertUploadState("idle");
    setIsoCertUploadError(undefined);
    form.setValue("iso_certification_document", null);
  }, [form]);

  const handleCeMarkingFile = useCallback(
    async (file: SelectedFile) => {
      setCeMarkingFile(file);
      setCeMarkingUploadState("uploading");
      setCeMarkingUploadError(undefined);
      try {
        const key = await preVettingApi.uploadDocument(file);
        form.setValue("ce_marking_document", key);
        onFieldBlur();
        setCeMarkingUploadState("success");
      } catch {
        setCeMarkingUploadState("error");
        setCeMarkingUploadError("fileUpload.uploadFailed");
      }
    },
    [form, onFieldBlur]
  );

  const handleCeMarkingRemove = useCallback(() => {
    setCeMarkingFile(null);
    setCeMarkingUploadState("idle");
    setCeMarkingUploadError(undefined);
    form.setValue("ce_marking_document", null);
  }, [form]);

  const handleQualityReportFile = useCallback(
    async (file: SelectedFile) => {
      setQualityReportFile(file);
      setQualityReportUploadState("uploading");
      setQualityReportUploadError(undefined);
      try {
        const key = await preVettingApi.uploadDocument(file);
        form.setValue("quality_test_report_document", key);
        onFieldBlur();
        setQualityReportUploadState("success");
      } catch {
        setQualityReportUploadState("error");
        setQualityReportUploadError("fileUpload.uploadFailed");
      }
    },
    [form, onFieldBlur]
  );

  const handleQualityReportRemove = useCallback(() => {
    setQualityReportFile(null);
    setQualityReportUploadState("idle");
    setQualityReportUploadError(undefined);
    form.setValue("quality_test_report_document", null);
  }, [form]);

  const handleEcoCertFile = useCallback(
    async (file: SelectedFile) => {
      setEcoCertFile(file);
      setEcoCertUploadState("uploading");
      setEcoCertUploadError(undefined);
      try {
        const key = await preVettingApi.uploadDocument(file);
        form.setValue("eco_certification_document", key);
        onFieldBlur();
        setEcoCertUploadState("success");
      } catch {
        setEcoCertUploadState("error");
        setEcoCertUploadError("fileUpload.uploadFailed");
      }
    },
    [form, onFieldBlur]
  );

  const handleEcoCertRemove = useCallback(() => {
    setEcoCertFile(null);
    setEcoCertUploadState("idle");
    setEcoCertUploadError(undefined);
    form.setValue("eco_certification_document", null);
  }, [form]);

  const handleBack = useCallback(() => {
    router.back();
  }, [router]);

  const handleNext = form.handleSubmit(async (values) => {
    if (!declarationChecked) {
      setDeclarationError(true);
      return;
    }
    await saveDraftMutation.mutateAsync({ step3: values });
    router.push("/(vendor)/pre-vetting/step-4");
  });

  return {
    form,
    onFieldBlur,
    handleNext,
    handleBack,
    declarationChecked,
    setDeclarationChecked,
    declarationError,
    setDeclarationError,
    isoCertFile,
    isoCertUploadState,
    isoCertUploadError,
    handleIsoCertFile,
    handleIsoCertRemove,
    ceMarkingFile,
    ceMarkingUploadState,
    ceMarkingUploadError,
    handleCeMarkingFile,
    handleCeMarkingRemove,
    qualityReportFile,
    qualityReportUploadState,
    qualityReportUploadError,
    handleQualityReportFile,
    handleQualityReportRemove,
    ecoCertFile,
    ecoCertUploadState,
    ecoCertUploadError,
    handleEcoCertFile,
    handleEcoCertRemove,
    // ── Picker ──────────────────────────────────────────────────────────────
    industryPickerVisible,
    openIndustryPicker: () => setIndustryPickerVisible(true),
    closeIndustryPicker: () => setIndustryPickerVisible(false),
    subcategoryPickerVisible,
    openSubcategoryPicker: () => setSubcategoryPickerVisible(true),
    closeSubcategoryPicker: () => setSubcategoryPickerVisible(false),
    allIndustries: industriesQuery.data ?? [],
    industriesLoading: industriesQuery.isLoading,
    allSubcategories: subcategoriesQuery.data ?? [],
    subcategoriesLoading: subcategoriesQuery.isLoading,
    saveIndustries,
    saveSubcategories,
    pickerLocale,
    togglePickerLocale,
  };
}
